import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getSession, clearSession } from './lib/auth';
import { Library } from './pages/Library';
import { SkillEditor } from './pages/SkillEditor';
import { Settings } from './pages/Settings';
import { Variables } from './pages/Variables';
import { Community } from './pages/Community';
import { CreateSkill } from './pages/CreateSkill';
import { AuthCallback } from './pages/AuthCallback';
import { Login } from './pages/Login';
import { TeamSkill } from './pages/TeamSkill';
import { TeamDraftEdit } from './pages/TeamDraft';
import { Inbox } from './pages/Inbox';
import { Review } from './pages/Review';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = getSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const session = getSession();
  const navigate = useNavigate();
  const handleSignOut = () => {
    clearSession();
    navigate('/login', { replace: true });
  };
  return (
    <div className="flex h-full flex-col">
      <header className="topbar">
        <div className="flex items-center gap-2 font-mono text-[13px] font-semibold">
          skillsou
        </div>
        <div className="text-center text-text-muted">
          {/* Topbar title slot — pages can set this later via a portal. */}
        </div>
        <div className="flex items-center gap-3 justify-self-end text-[12px] text-text-muted">
          {session ? (
            <>
              <span className="font-mono">{session.email}</span>
              <button
                type="button"
                onClick={handleSignOut}
                className="btn btn-icon"
                title="sign out"
                aria-label="sign out"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="btn"
            >
              Sign in
            </button>
          )}
        </div>
      </header>
      <main className="flex-1 overflow-hidden bg-canvas">{children}</main>
    </div>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  if (!ready) return null;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/library"
        element={
          <RequireAuth>
            <AppShell>
              <Library />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/skill/:id"
        element={
          <RequireAuth>
            <AppShell>
              <SkillEditor />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/create"
        element={
          <RequireAuth>
            <AppShell>
              <CreateSkill />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/cli"
        element={
          <RequireAuth>
            <AppShell>
              <Settings />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/settings/variables"
        element={
          <RequireAuth>
            <AppShell>
              <Variables />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/community"
        element={
          <AppShell>
            <Community />
          </AppShell>
        }
      />
      <Route
        path="/orgs/:orgId/skills/:skillId"
        element={
          <RequireAuth>
            <AppShell>
              <TeamSkill />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/orgs/:orgId/drafts/:draftId/edit"
        element={
          <RequireAuth>
            <AppShell>
              <TeamDraftEdit />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/orgs/:orgId/inbox"
        element={
          <RequireAuth>
            <AppShell>
              <Inbox />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/orgs/:orgId/drafts/:draftId/review"
        element={
          <RequireAuth>
            <AppShell>
              <Review />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route path="/" element={<Navigate to="/library" replace />} />
      <Route path="*" element={<Navigate to="/library" replace />} />
    </Routes>
  );
}
